// client/src/pages/CampDetailPage.jsx
// Single-camp dashboard. Header card with breadcrumb + identity, pill-style
// subnav, and a per-tab section beneath. Overview tab now leads with a
// quick-actions grid so a manager landing here has a one-click jump to
// every tool — they don't have to read the tab strip first.

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useCamp, useUpdateCamp, useDeleteCamp } from '../hooks/useCamps.js';
import { usePageMenu } from '../components/Layout.jsx';
import Spinner from '../components/Spinner.jsx';
import Modal from '../components/Modal.jsx';
import CampForm from '../components/CampForm.jsx';
import ManagersTab from '../components/ManagersTab.jsx';
import StudentsTab from '../components/StudentsTab.jsx';
import ClassesTab from '../components/ClassesTab.jsx';
import DevicesTab from '../components/DevicesTab.jsx';
import TransactionsTab from '../components/TransactionsTab.jsx';
import LeaderboardTab from '../components/LeaderboardTab.jsx';
import ReportsTab from '../components/ReportsTab.jsx';
import CatalogTab from '../components/CatalogTab.jsx';
import VouchersTab from '../components/VouchersTab.jsx';
import ModulesTab from '../components/ModulesTab.jsx';
import PromoteWizard from '../components/PromoteWizard.jsx';
import { formatDate } from '../lib/format.js';
import {
  HomeIcon,
  UserGroupIcon,
  AcademicCapIcon,
  BuildingLibraryIcon,
  DevicePhoneMobileIcon,
  ClipboardDocumentListIcon,
  ArrowsRightLeftIcon,
  TrophyIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  WrenchScrewdriverIcon,
} from '../components/Icons.jsx';

const TABS = [
  { id: 'overview',     label: 'סקירה',     Icon: HomeIcon },
  { id: 'managers',     label: 'מנהלים',    Icon: UserGroupIcon },
  { id: 'students',     label: 'תלמידים',   Icon: AcademicCapIcon },
  { id: 'classes',      label: 'כיתות',     Icon: BuildingLibraryIcon },
  { id: 'devices',      label: 'מכשירים',   Icon: DevicePhoneMobileIcon },
  { id: 'catalog',      label: 'קטלוג',     Icon: ClipboardDocumentListIcon },
  { id: 'vouchers',     label: 'שוברים',    Icon: CreditCardIcon },
  { id: 'transactions', label: 'עסקאות',    Icon: ArrowsRightLeftIcon },
  { id: 'leaderboard',  label: 'מובילים',   Icon: TrophyIcon },
  { id: 'reports',      label: 'דוחות',     Icon: ChartBarIcon },
  { id: 'modules',      label: 'מודולים',   Icon: WrenchScrewdriverIcon },
  // 'settings' is intentionally NOT a tab — it's reached via the gear button by
  // the camp name (and the "מעבר להגדרות" button on the overview), to keep the
  // tab strip from overflowing.
];

// Sections that are valid URL segments (the tabs + settings). Anything else in
// the :tab param falls back to the overview.
const VALID_TABS = new Set([...TABS.map((t) => t.id), 'settings']);

// Quick-action tiles shown on the Overview tab. Tone keys match the
// design-system palettes; the strap colour cycles the same way the
// camp-card accents do, so the visual language stays consistent.
const QUICK_ACTIONS = [
  { id: 'students',     label: 'תלמידים',     hint: 'הוסף, ערוך, ייבא מאקסל', Icon: AcademicCapIcon,              tone: 'brand' },
  { id: 'classes',      label: 'כיתות',       hint: 'שכבות, מחנכים, הערות',   Icon: BuildingLibraryIcon,          tone: 'sky'   },
  { id: 'devices',      label: 'מכשירים',     hint: 'מסופונים — זיכוי וחנות', Icon: DevicePhoneMobileIcon,        tone: 'mint'  },
  { id: 'catalog',      label: 'קטלוג פרסים', hint: 'מה אפשר לקנות, כמה זה',  Icon: ClipboardDocumentListIcon,    tone: 'gold'  },
  { id: 'vouchers',     label: 'שוברים',      hint: 'הפקה, הדפסה, מימוש',     Icon: CreditCardIcon,               tone: 'joy'   },
  { id: 'transactions', label: 'עסקאות',      hint: 'היסטוריה ופילטרים',       Icon: ArrowsRightLeftIcon,          tone: 'sky'   },
  { id: 'leaderboard',  label: 'מובילים',     hint: 'דירוג חי, פודיום',        Icon: TrophyIcon,                   tone: 'clay'  },
  { id: 'reports',      label: 'דוחות',       hint: 'ייצוא לאקסל',             Icon: ChartBarIcon,                 tone: 'joy'   },
  { id: 'modules',      label: 'מודולים',     hint: 'נוכחות ועוד — הפעלה והגדרה', Icon: WrenchScrewdriverIcon,     tone: 'mint'  },
  // External link — routes to /camps/:campId/order-cards instead of
  // switching tabs. `href: true` flips the renderer to a <Link>.
  { id: 'order-cards',  label: 'הזמנת ציוד',  hint: 'כרטיסים מגנטיים · בעתיד גם מסופונים', Icon: CreditCardIcon, tone: 'sky', href: true },
];

const TONE_CLASSES = {
  brand: 'bg-brand-100 text-brand-700',
  mint:  'bg-mint-100  text-mint-700',
  gold:  'bg-gold-100  text-gold-700',
  sky:   'bg-sky-100   text-sky-700',
  clay:  'bg-clay-100  text-clay-700',
  joy:   'bg-joy-100   text-joy-700',
};

function campStatus(camp) {
  const now = Date.now();
  const start = camp.startDate ? new Date(camp.startDate).getTime() : null;
  const end   = camp.endDate   ? new Date(camp.endDate).getTime()   : null;
  if (start && now < start) return { label: 'עוד לא התחילה', tone: 'badge-slate' };
  if (end && now > end)     return { label: 'הסתיימה',       tone: 'badge-slate' };
  if (start && (!end || now <= end)) return { label: 'פעילה עכשיו', tone: 'badge-mint' };
  return { label: 'פתוחה', tone: 'badge-indigo' };
}

export default function CampDetailPage({ user }) {
  const { campId, tab: tabParam } = useParams();
  const navigate = useNavigate();
  const camp = useCamp(campId);
  // CatalogTab calls back when it has unsaved edits; we use the flag to
  // confirm tab switches so a stray click doesn't drop catalog changes.
  // Tabs without unsaved-state callbacks contribute false (no friction).
  const [dirty, setDirty] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false); // mobile section menu
  const { setOpener } = usePageMenu();

  // The active section is driven by the URL — deep-linkable, browser back/forward
  // works, and a refresh keeps the tab. Unknown/empty segment → overview.
  const tab = VALID_TABS.has(tabParam) ? tabParam : 'overview';

  // Expose this page's drawer to the global header's hamburger (next to the
  // logo), so the menu is reachable from the very top too. Cleared on unmount.
  useEffect(() => {
    setOpener(() => () => setMenuOpen(true));
    return () => setOpener(null);
  }, [setOpener]);

  // Warn on refresh / tab-close / leaving the site while a tab has unsaved
  // edits (e.g. the catalog). In-app tab switches are guarded separately in
  // tryChangeTab; this covers the browser-level exits.
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  function tryChangeTab(nextId) {
    if (nextId === tab) { setMenuOpen(false); return; }
    if (dirty) {
      const ok = window.confirm(
        'יש שינויים שלא נשמרו בלשונית הנוכחית.\nלעבור בלי לשמור?'
      );
      if (!ok) return;
      setDirty(false); // leaving the editing tab discards its unsaved state
    }
    navigate(`/camps/${campId}/${nextId}`);
  }

  if (camp.isLoading) return <Spinner label="טוען תכנית..." />;
  if (camp.isError) {
    return (
      <div className="card-danger p-5 text-sm">
        <p className="text-joy-900 font-medium">
          {camp.error.status === 404 ? 'התכנית לא נמצאה.' : `שגיאה: ${camp.error.message}`}
        </p>
        <Link to="/" className="btn-secondary btn-sm mt-3">חזרה לרשימה</Link>
      </div>
    );
  }
  if (!camp.data) return null;

  const status = campStatus(camp.data);
  // Active section's label/icon for the mobile menu trigger. 'settings' isn't a
  // tab (reached via the gear), so fall back to its label/icon when selected.
  const activeTab = TABS.find((t) => t.id === tab);
  const activeLabel = activeTab ? activeTab.label : 'הגדרות';
  const ActiveIcon = activeTab ? activeTab.Icon : Cog6ToothIcon;

  return (
    <div className="space-y-6">
      {/* HERO — breadcrumb + identity + status. No wrapping card: the bare
          heading reads cleaner and doesn't eat vertical space. */}
      <header>
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:text-brand-900"
        >
          <span aria-hidden>←</span> חזרה לכל התכניות
        </Link>
        <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex items-center gap-3 sm:gap-4">
            {camp.data.hasLogo && (
              <img
                src={`/api/camps/${camp.data.id}/logo?v=${
                  camp.data.logoUpdatedAt ? new Date(camp.data.logoUpdatedAt).getTime() : 0
                }`}
                alt=""
                className="w-14 h-14 sm:w-16 sm:h-16 shrink-0 rounded-2xl object-contain bg-white border border-ink-100"
              />
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="h-page">{camp.data.name}</h1>
                <button
                  type="button"
                  onClick={() => tryChangeTab('settings')}
                  title="הגדרות התכנית"
                  aria-label="הגדרות התכנית"
                  className={`shrink-0 w-9 h-9 rounded-xl grid place-items-center transition-colors ${
                    tab === 'settings'
                      ? 'bg-brand-600 text-white'
                      : 'text-ink-500 hover:bg-ink-100 hover:text-ink-900'
                  }`}
                >
                  <Cog6ToothIcon className="w-5 h-5" />
                </button>
              </div>
              {camp.data.description && (
                <p className="mt-2 text-muted max-w-2xl">{camp.data.description}</p>
              )}
            </div>
          </div>
          <span className={`${status.tone} text-sm`}>{status.label}</span>
        </div>
        {(camp.data.startDate || camp.data.endDate) && (
          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            {camp.data.startDate && (
              <span className="badge-slate num">החל מ-{formatDate(camp.data.startDate)}</span>
            )}
            {camp.data.endDate && (
              <span className="badge-slate num">עד {formatDate(camp.data.endDate)}</span>
            )}
          </div>
        )}
      </header>

      {/* Mobile: a hamburger bar; tapping it opens a RIGHT-SIDE drawer with all
          sections (full names). Desktop (sm+): the wrapped pill strip below. */}
      <div className="sm:hidden">
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          className="w-full flex items-center justify-between gap-2 card px-4 py-3"
          aria-label="פתיחת תפריט"
          aria-haspopup="menu"
        >
          <span className="flex items-center gap-2 font-bold text-ink-900">
            <ActiveIcon className="w-5 h-5 text-brand-600" />
            {activeLabel}
          </span>
          <HamburgerIcon className="w-6 h-6 text-ink-600" />
        </button>
      </div>

      {menuOpen && createPortal(
        <div className="fixed inset-0 z-50 sm:hidden">
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className="absolute inset-0 bg-ink-900/40 animate-fade-in"
            onClick={() => setMenuOpen(false)}
          />
          {/* Drawer pinned to the inline-start edge = the RIGHT in RTL. */}
          <div className="absolute inset-y-0 start-0 w-72 max-w-[82%] bg-white shadow-pop flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-ink-100">
              <span className="font-bold text-ink-900">תפריט</span>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label="סגירת תפריט"
                className="w-9 h-9 grid place-items-center rounded-xl text-ink-500 hover:bg-ink-100 hover:text-ink-900"
              >
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto p-2 space-y-1" role="menu">
              {TABS.map((t) => {
                const active = tab === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    role="menuitem"
                    onClick={() => { tryChangeTab(t.id); setMenuOpen(false); }}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-start transition-colors ${
                      active ? 'bg-brand-600 text-white' : 'text-ink-800 hover:bg-ink-100'
                    }`}
                  >
                    <t.Icon className="w-5 h-5 shrink-0" />
                    <span className="font-semibold">{t.label}</span>
                  </button>
                );
              })}
              <div className="my-1.5 border-t border-ink-100" />
              <button
                type="button"
                role="menuitem"
                onClick={() => { tryChangeTab('settings'); setMenuOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-start transition-colors ${
                  tab === 'settings' ? 'bg-brand-600 text-white' : 'text-ink-800 hover:bg-ink-100'
                }`}
              >
                <Cog6ToothIcon className="w-5 h-5 shrink-0" />
                <span className="font-semibold">הגדרות</span>
              </button>
            </nav>
          </div>
        </div>,
        document.body
      )}

      {/* Desktop: wrapped pill strip */}
      <nav className="hidden sm:flex sm:flex-wrap gap-1.5">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => tryChangeTab(t.id)}
            className={tab === t.id ? 'tab-active' : 'tab'}
          >
            <t.Icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </nav>

      <section>
        {tab === 'overview' && (
          <OverviewTab camp={camp.data} onPick={tryChangeTab} />
        )}
        {tab === 'managers' && <ManagersTab user={user} campId={camp.data.id} />}
        {tab === 'students' && <StudentsTab campId={camp.data.id} />}
        {tab === 'classes' && <ClassesTab campId={camp.data.id} />}
        {tab === 'devices' && <DevicesTab campId={camp.data.id} />}
        {tab === 'catalog' && (
          <CatalogTab
            campId={camp.data.id}
            currency={camp.data.currency}
            onDirtyChange={setDirty}
          />
        )}
        {tab === 'vouchers' && (
          <VouchersTab campId={camp.data.id} currency={camp.data.currency} />
        )}
        {tab === 'transactions' && (
          <TransactionsTab campId={camp.data.id} currency={camp.data.currency} />
        )}
        {tab === 'leaderboard' && (
          <LeaderboardTab campId={camp.data.id} currency={camp.data.currency} />
        )}
        {tab === 'reports' && (
          <ReportsTab campId={camp.data.id} currency={camp.data.currency} />
        )}
        {tab === 'modules' && <ModulesTab campId={camp.data.id} />}
        {tab === 'settings' && <SettingsTab user={user} camp={camp.data} />}
      </section>
    </div>
  );
}

function OverviewTab({ camp, onPick }) {
  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {QUICK_ACTIONS.map((a) => {
          const inner = (
            <>
              <div className={`w-11 h-11 rounded-2xl grid place-items-center mb-3 ${TONE_CLASSES[a.tone]}`}>
                <a.Icon className="w-5 h-5" />
              </div>
              <div className="font-bold text-ink-900">{a.label}</div>
              <div className="text-sm text-ink-600 mt-0.5">{a.hint}</div>
            </>
          );
          const cls = 'card text-start p-5 hover:shadow-pop hover:-translate-y-0.5 hover:border-brand-200 transition-all block';
          return a.href ? (
            <Link key={a.id} to={`/camps/${camp.id}/${a.id}`} className={cls}>{inner}</Link>
          ) : (
            <button key={a.id} type="button" onClick={() => onPick(a.id)} className={cls}>{inner}</button>
          );
        })}
      </div>

      <div className="card p-6">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="h-section">פרטי תכנית</h2>
          <button type="button" className="btn-secondary btn-sm" onClick={() => onPick('settings')}>
            <Cog6ToothIcon className="w-4 h-4" />
            מעבר להגדרות
          </button>
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <Row k="שם"      v={camp.name} />
          <Row k="תיאור"   v={camp.description || '—'} />
          <Row k="תחילה"   v={formatDate(camp.startDate)} />
          <Row k="סיום"    v={formatDate(camp.endDate)} />
          <Row k="נוצרה ב-" v={formatDate(camp.createdAt)} />
          <Row k="מטבע"    v={camp.currency} />
        </dl>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-ink-100 last:border-0">
      <dt className="text-ink-500 font-medium">{k}</dt>
      <dd className="text-ink-900 text-end">{v}</dd>
    </div>
  );
}

// Hamburger ("☰") — the mobile drawer trigger.
function HamburgerIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}
function CloseIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function SettingsTab({ user, camp }) {
  const [editing, setEditing] = useState(false);
  const [editDirty, setEditDirty] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const updateMutation = useUpdateCamp(camp.id);
  const deleteMutation = useDeleteCamp();

  return (
    <div className="space-y-4">
      <div className="card p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="h-section">עריכת פרטים</h2>
            <p className="mt-1 text-muted">שינוי שם, תיאור, או תאריכי תכנית.</p>
          </div>
          <button type="button" className="btn-secondary" onClick={() => setEditing(true)}>
            ערוך
          </button>
        </div>
      </div>

      <div className="card p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="h-section">מעבר לשנה חדשה</h2>
            <p className="mt-1 text-muted">
              העתקת כל התלמידים לתכנית של השנה החדשה כולל מספרי הכרטיסים והקפצת
              כיתה. הכרטיסים ינותקו מהתכנית הנוכחית.
            </p>
            {!user.isSuperAdmin && (
              <p className="mt-1 text-xs text-ink-500">
                לפתיחת תכנית לשנה החדשה יש לפנות למשרד.
              </p>
            )}
          </div>
          <button type="button" className="btn-secondary" onClick={() => setPromoting(true)}>
            מעבר לשנה חדשה
          </button>
        </div>
      </div>

      {user.isSuperAdmin && (
        <div className="card-danger p-6">
          <h2 className="h-section text-joy-900">אזור מסוכן</h2>
          <p className="mt-2 text-sm text-joy-800">
            מחיקת התכנית תארכב אותה (soft-delete). הנתונים נשמרים אך התכנית
            לא תופיע יותר ברשימות. ניתן לשחזר רק על ידי DBA.
          </p>
          <button
            type="button"
            className="btn-danger mt-4"
            onClick={() => setConfirmDelete(true)}
          >
            ארכב תכנית
          </button>
        </div>
      )}

      <PromoteWizard
        open={promoting}
        onClose={() => setPromoting(false)}
        campId={camp.id}
        campName={camp.name}
        user={user}
      />

      <Modal
        open={editing}
        onClose={() => setEditing(false)}
        isDirty={editDirty}
        title="עריכת תכנית"
      >
        <CampForm
          initial={camp}
          submitLabel="שמור שינויים"
          onDirtyChange={setEditDirty}
          onCancel={() => setEditing(false)}
          onSubmit={async (body) => {
            await updateMutation.mutateAsync(body);
            setEditing(false);
          }}
        />
      </Modal>

      <Modal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="לארכב את התכנית?"
      >
        <p className="text-sm text-ink-700">
          האם אתה בטוח שברצונך לארכב את התכנית <strong>{camp.name}</strong>?
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={() => setConfirmDelete(false)}>
            ביטול
          </button>
          <button
            type="button"
            className="btn-danger"
            disabled={deleteMutation.isPending}
            onClick={async () => {
              await deleteMutation.mutateAsync(camp.id);
              window.location.assign('/');
            }}
          >
            {deleteMutation.isPending ? 'מארכב...' : 'כן, ארכב'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
